import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Category, MoodPreset, StylePreset } from '@/lib/types'
import { PageHeader } from '@/components/AppLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input, Textarea } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ErrorNote } from './DashboardPage'

type Tab = 'categories' | 'styles' | 'moods'

/**
 * Catalogue administration.
 *
 * This screen is where Pixviro's prompt engineering lives in production:
 * editing a prompt fragment changes what the model produces for every user on
 * the next generation, with no app release. That is the point, and also why
 * every change here is audited.
 */
export function CatalogPage() {
  const [tab, setTab] = useState<Tab>('categories')
  const queryClient = useQueryClient()

  const categories = useQuery({
    queryKey: ['catalog', 'categories'],
    queryFn: () => api.get<Category[]>('api/admin/catalog/categories'),
  })
  const styles = useQuery({
    queryKey: ['catalog', 'styles'],
    queryFn: () => api.get<StylePreset[]>('api/admin/catalog/styles'),
  })
  const moods = useQuery({
    queryKey: ['catalog', 'moods'],
    queryFn: () => api.get<MoodPreset[]>('api/admin/catalog/moods'),
  })

  const [editing, setEditing] = useState<{ kind: Tab; item: Category | StylePreset | MoodPreset } | null>(null)

  const save = useMutation({
    mutationFn: ({ kind, item }: { kind: Tab; item: Record<string, unknown> }) => {
      const path =
        kind === 'categories'
          ? `api/admin/catalog/categories/${item.id as string}`
          : kind === 'styles'
            ? `api/admin/catalog/styles/${item.id as string}`
            : `api/admin/catalog/moods/${item.id as string}`
      return api.put(path, item)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['catalog'] })
      setEditing(null)
    },
  })

  const error = categories.error ?? styles.error ?? moods.error ?? save.error

  return (
    <>
      <PageHeader
        title="Catalogue"
        description="Interests, styles and moods — including the prompt fragments the server injects."
      />

      <div className="space-y-4 p-8">
        {error && <ErrorNote message={(error as Error).message} />}

        <div className="flex gap-2">
          {(['categories', 'styles', 'moods'] as const).map((candidate) => (
            <Button
              key={candidate}
              variant={tab === candidate ? 'default' : 'secondary'}
              size="sm"
              onClick={() => setTab(candidate)}
            >
              {candidate[0]!.toUpperCase() + candidate.slice(1)}
            </Button>
          ))}
        </div>

        <div className="grid gap-3">
          {tab === 'categories' &&
            categories.data?.map((item) => (
              <CatalogRow
                key={item.id}
                title={item.nameEn}
                slug={item.slug}
                active={item.active}
                fragment={item.promptFragment}
                onEdit={() => setEditing({ kind: 'categories', item })}
              />
            ))}
          {tab === 'styles' &&
            styles.data?.map((item) => (
              <CatalogRow
                key={item.id}
                title={item.nameEn}
                slug={item.slug}
                active={item.active}
                fragment={item.promptFragment}
                onEdit={() => setEditing({ kind: 'styles', item })}
              />
            ))}
          {tab === 'moods' &&
            moods.data?.map((item) => (
              <CatalogRow
                key={item.id}
                title={item.nameEn}
                slug={item.slug}
                active={item.active}
                fragment={item.promptFragment || '(resolved to a random mood at generation time)'}
                onEdit={() => setEditing({ kind: 'moods', item })}
              />
            ))}
        </div>
      </div>

      <EditDialog
        editing={editing}
        pending={save.isPending}
        onClose={() => setEditing(null)}
        onSave={(item) => editing && save.mutate({ kind: editing.kind, item })}
      />
    </>
  )
}

function CatalogRow({
  title,
  slug,
  active,
  fragment,
  onEdit,
}: {
  title: string
  slug: string
  active: boolean
  fragment: string
  onEdit: () => void
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-base text-[var(--color-text-primary)]">{title}</CardTitle>
          <div className="mt-1 flex items-center gap-2">
            <code className="text-xs text-[var(--color-text-tertiary)]">{slug}</code>
            <Badge variant={active ? 'success' : 'default'}>{active ? 'Active' : 'Inactive'}</Badge>
          </div>
        </div>
        <Button variant="secondary" size="sm" onClick={onEdit}>
          Edit
        </Button>
      </CardHeader>
      <CardContent>
        <p className="line-clamp-2 text-xs text-[var(--color-text-secondary)]">{fragment}</p>
      </CardContent>
    </Card>
  )
}

function EditDialog({
  editing,
  pending,
  onClose,
  onSave,
}: {
  editing: { kind: Tab; item: Category | StylePreset | MoodPreset } | null
  pending: boolean
  onClose: () => void
  onSave: (item: Record<string, unknown>) => void
}) {
  const [draft, setDraft] = useState<Record<string, unknown>>({})

  // Reset the draft whenever a different row is opened.
  const key = editing?.item.id ?? ''
  const [lastKey, setLastKey] = useState('')
  if (key !== lastKey) {
    setLastKey(key)
    setDraft(editing ? ({ ...editing.item } as Record<string, unknown>) : {})
  }

  if (!editing) return null

  const field = (name: string) => String(draft[name] ?? '')
  const update = (name: string, value: unknown) => setDraft((current) => ({ ...current, [name]: value }))

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit {field('slug')}</DialogTitle>
          <DialogDescription>
            The prompt fragment is never sent to clients. Changes take effect on the next generation across
            all users, and are recorded in the audit log.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-3">
            <Input value={field('nameEn')} onChange={(e) => update('nameEn', e.target.value)} placeholder="English" />
            <Input value={field('nameDe')} onChange={(e) => update('nameDe', e.target.value)} placeholder="German" />
            <Input value={field('nameEs')} onChange={(e) => update('nameEs', e.target.value)} placeholder="Spanish" />
          </div>

          <Textarea
            className="min-h-32"
            value={field('promptFragment')}
            onChange={(e) => update('promptFragment', e.target.value)}
            placeholder="Prompt fragment"
          />

          {editing.kind === 'styles' && (
            <Textarea
              value={field('avoidFragment')}
              onChange={(e) => update('avoidFragment', e.target.value)}
              placeholder="Artefacts to avoid (optional)"
            />
          )}

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={Boolean(draft.active)}
                onChange={(e) => update('active', e.target.checked)}
              />
              Active
            </label>
            <Input
              type="number"
              className="w-28"
              value={field('sortOrder')}
              onChange={(e) => update('sortOrder', Number.parseInt(e.target.value, 10) || 0)}
              placeholder="Order"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={pending} onClick={() => onSave(draft)}>
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
